import type { WishlistsFavorites, WishlistsFavoritesItem } from '#root/api/clients/wishlists/data-contracts'

/**
 * Favorites (Wishlist) Store - manages user's saved/favorited products across brands.
 *
 * **Key behaviors:**
 * - Persists favoriteId to survive page reloads
 * - Syncs with backend wishlists API on add/remove
 * - Disabled for in-store employee sessions (BOPIS)
 * - Supports pagination for large favorites lists
 * - Per-country store isolation (parameterized by countryCode)
 *
 * **Multi-brand notes:**
 * - All brands use same favorites mechanism
 * - Feature can be toggled via LaunchDarkly flag (allowFavorites)
 * - Guest favorites convert to logged-in user favorites on login
 *
 * **Architecture:**
 * - Store initialized via favorites plugin (domains/commerce/plugins/favorites.ts)
 * - Country-specific stores via factory pattern: `useFavoritesStore(countryCode)`
 * - Persists only favoriteId (full list loaded on demand)
 *
 * @see domains/commerce/plugins/favorites.ts for initialization
 * @see domains/commerce/components/product/FavoriteButton.vue for UI integration
 * @see CONTRIBUTING.md#vuenuxt-composables---critical-rules for setup-only composable rules
 */

// this store is initialized on the favorites plugin
export const useFavoritesStore = (countryCode = useCountryCode()) => defineStore(`favorites_${countryCode}`, () => {
  const buyInStore = useBuyInStoreStore()
  const { allowFavorites } = useFeatureFlags()
  const {
    list: { productsPerPage },
    maxItems
  } = useAppConfig().components.favorites
  const { wishlists } = useApi()

  // ─────────────────────────────────────────────────────────────────────────
  // State
  // ─────────────────────────────────────────────────────────────────────────

  /** Error state for failed API operations */
  const error = ref(false)

  /** Backend-generated favorite list ID (persisted) */
  const favoriteId = ref('')

  /** Array of favorited product items with metadata */
  const favorites = reactive<WishlistsFavoritesItem[]>([])

  /** Total count of favorites (may exceed loaded items if paginated) */
  const favoritesCount = ref(0)

  /** Loading state for async operations (add, remove, load) */
  const pending = ref(false)

  // ─────────────────────────────────────────────────────────────────────────
  // Getters
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Check if a product is in the favorites list.
   *
   * @param {string} id - Product ID to check
   * @returns {boolean} true if product is favorited
   *
   * @example
   * ```ts
   * const favStore = useFavoritesStore()
   * if (favStore.isFavorite('product-123')) {
   *   // Show filled heart icon
   * }
   * ```
   */
  const isFavorite = (id: string) => favorites.some((item) => item.id === id)

  // ─────────────────────────────────────────────────────────────────────────
  // Pagination Query
  // ─────────────────────────────────────────────────────────────────────────

  /** Pagination parameters for favorites list API */
  const query = reactive({
    start: 0,
    count: productsPerPage
  })

  // ─────────────────────────────────────────────────────────────────────────
  // Actions
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Load favorites from backend API.
   *
   * **Behavior:**
   * - Fetches favorites using current pagination query
   * - If `query.start > 0`: appends unique items (pagination)
   * - If `query.start === 0`: replaces entire list
   * - Skipped if: feature disabled OR employee connected
   *
   * **Error handling:**
   * - Sets error flag on failure (logged to console)
   * - Does not throw (prevents UI crashes)
   *
   * @example
   * ```ts
   * const favStore = useFavoritesStore()
   * await favStore.load() // Initial load
   * favStore.query.start = 10
   * await favStore.load() // Load next page
   * ```
   */
  const load = async () => {
    if (!allowFavorites || buyInStore.employeeConnected)
      return

    try {
      pending.value = true
      const data: WishlistsFavorites = await wishlists.$getFavorites({ query })
      favoritesCount.value = data.total
      favoriteId.value = data.favoriteId || ''
      if (query.start && favorites.length) {
        // Pagination: append only new items
        const uniqueItems = data.items?.filter(
          (item) => !favorites.some(({ itemId }) => itemId === item.itemId)
        )
        favorites.push(...(uniqueItems || []))
      }
      else {
        // Initial load: replace entire array
        favorites.splice(0, favorites.length, ...(data.items || []))
      }
    }
    catch (e: any) {
      log.warn('Error loading favorites.', e)
      error.value = true
    }
    finally {
      pending.value = false
    }
  }

  /**
   * Set the favorite list ID and optionally reload favorites.
   *
   * **Use cases:**
   * - Guest → Logged-in user transition (login merges favorites)
   * - Logout (clear favorites by passing null)
   *
   * @param {string | null} value - New favoriteId (null to clear)
   *
   * ⚠️ **Side effect**: Triggers `load()` if value is truthy
   *
   * @example
   * ```ts
   * // On login (favorites plugin)
   * await favStore.setFavoriteId(authResponse.favorites.favoriteId)
   *
   * // On logout
   * await favStore.setFavoriteId(null)
   * ```
   */
  const setFavoriteId = async (value) => {
    favoriteId.value = value || ''
    if (value)
      await load()
    else
      favorites.length = 0
  }

  /**
   * Manually set favorites count (used by external sync logic).
   *
   * @param {number} count - New favorites count
   */
  const setFavoritesCount = (count) => {
    favoritesCount.value = count
  }

  /**
   * Add a product to favorites.
   *
   * **Behavior:**
   * - Creates backend favorite entry via API
   * - Resets pagination and reloads list on success
   * - Returns error flag (does not throw)
   *
   * **Restrictions:**
   * - Blocked if feature disabled
   * - Blocked if employee connected (BOPIS mode)
   *
   * @param {string} id - Product ID to add
   * @param {Record<string, any>} [props] - Additional metadata (e.g., variant, color)
   * @returns {Promise<{error: boolean, limitReached?: boolean}>} Success/failure indicator
   *
   * ⚠️ **Must be called in component setup** (composable rule)
   * ⚠️ **Cache the store reference**, don't call useFavoritesStore() repeatedly
   *
   * @example
   * ```ts
   * const favStore = useFavoritesStore() // Call ONCE in setup
   * const { error } = await favStore.add('product-123', { color: 'red' })
   * if (!error) {
   *   toast.success('Added to favorites!')
   * }
   * ```
   */
  const add = async (
    id: string,
    props?: Record<string, any>
  ): Promise<{ error: boolean, limitReached?: boolean }> => {
    if (!allowFavorites || buyInStore.employeeConnected)
      return { error: false }

    if (maxItems && favoritesCount.value >= maxItems)
      return { error: false, limitReached: true }

    pending.value = true
    try {
      const data = await wishlists.$wishlistsAddToFavorites({
        id,
        qty: 1,
        itemType: 'product',
        favoriteId: favoriteId.value,
        ...props // some things won't get populated on the backend
      })
      query.start = 0
      await setFavoriteId(data.favoriteId)
      return { error: false }
    }
    catch (e: any) {
      log.warn(e)
      return { error: true }
    }
    finally {
      pending.value = false
    }
  }

  /**
   * Remove a product from favorites.
   *
   * **Behavior:**
   * - Optimistically updates UI (removes from local array)
   * - Calls backend API to persist removal
   * - Decrements favoritesCount on success
   *
   * **Error handling:**
   * - Returns error flag (does not rollback optimistic update)
   * - Logs error to console
   *
   * @param {string} id - Product ID to remove
   * @returns {Promise<{error: boolean}>} Success/failure indicator
   *
   * @throws {Error} "Item not found" if product not in favorites array
   *
   * @example
   * ```ts
   * const favStore = useFavoritesStore()
   * const { error } = await favStore.remove('product-123')
   * if (error) {
   *   toast.error('Failed to remove from favorites')
   * }
   * ```
   */
  const remove = async (id: string) => {
    if (!allowFavorites || buyInStore.employeeConnected)
      return { error: false }

    pending.value = true

    try {
      const itemIndex = favorites.findIndex((i) => i.id === id)
      if (itemIndex === -1) throw (new Error('Item not found'))

      const itemId = favorites[itemIndex].itemId
      await wishlists.$removeFavoritesItem(favoriteId.value, itemId)

      favorites.splice(itemIndex, 1)
      favoritesCount.value--

      return { error: false }
    }
    catch (err) {
      log.warn(err)
      return { error: true }
    }
    finally {
      pending.value = false
    }
  }

  return {
    add,
    error,
    favoriteId,
    favorites,
    favoritesCount,
    isFavorite,
    load,
    pending,
    query,
    remove,
    setFavoriteId,
    setFavoritesCount
  }
}, {
  persist: {
    pick: ['favoriteId']
  }
})()
